import type { TiebreakerTeam, TiebreakerStepFn, StepResult, GameResult, GameResultsLookup } from '../types';
import { RankMethod } from '../../enums/rank-method';
import { SelectionType } from '../../enums/selection-type';
import { addExplanation } from '../helpers';

/**
 * NHL Head-to-Head Scoring (N-team).
 *
 * Computes the percentage of available points earned in head-to-head games
 * among the tied group, per NHL official tiebreaking rules:
 *   - Regular win: 2 pts; loser 0 pts.
 *   - OT/SO win: 2 pts; loser 1 pt (OTL/SOL).
 *   - "Available points" = 2 per game counted.
 *
 * Odd-game exclusion (NHL rule §14): when one team hosts more games than the
 * other within the sub-group, the earliest game(s) in the city with the extra
 * game are excluded before computing the percentage. Games without a sortOrder
 * are treated as undated — exclusion is skipped for that pair.
 *
 * The NHL ranker uses every club's percentage against the same tied group.
 *
 * Works for 2-team and N-team groups. For a 2-team group the percentage
 * approach is equivalent to the previous pts-comparison approach.
 */
export interface H2hScoringRecord {
  points: number;
  available: number;
  percentage: number;
}

export function calculateH2hScoringRecords<T extends TiebreakerTeam>(
  teams: T[],
  games: GameResultsLookup,
): { records: Map<string, H2hScoringRecord>; anyGames: boolean } {
  const pts = new Map<string, number>();
  const avail = new Map<string, number>();
  for (const t of teams) {
    pts.set(t.teamAbbreviation, 0);
    avail.set(t.teamAbbreviation, 0);
  }

  let anyGames = false;

  // Evaluate every ordered pair (i < j) within the group.
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) {
      const t1 = teams[i];
      const t2 = teams[j];
      const a1 = t1.teamAbbreviation;
      const a2 = t2.teamAbbreviation;

      const allGames = games.getGamesBetween(a1, a2);
      if (allGames.length === 0) {
        continue;
      }

      const counted = excludeOddGames(allGames, a1);
      if (counted.length === 0) {
        continue;
      }

      anyGames = true;

      for (const g of counted) {
        if (g.winner == null) {
          continue; // unplayed game
        }

        avail.set(a1, (avail.get(a1) ?? 0) + 2);
        avail.set(a2, (avail.get(a2) ?? 0) + 2);

        const selType = g.selectionType ?? SelectionType.NONE;
        const isExtra = selType === SelectionType.HOME_WIN_OVERTIME ||
          selType === SelectionType.HOME_WIN_SHOOTOUT ||
          selType === SelectionType.AWAY_WIN_OVERTIME ||
          selType === SelectionType.AWAY_WIN_SHOOTOUT;

        if (g.winner === a1) {
          pts.set(a1, (pts.get(a1) ?? 0) + 2);
          if (isExtra) {
            pts.set(a2, (pts.get(a2) ?? 0) + 1); // OTL/SOL earns 1
          }
        } else if (g.winner === a2) {
          pts.set(a2, (pts.get(a2) ?? 0) + 2);
          if (isExtra) {
            pts.set(a1, (pts.get(a1) ?? 0) + 1);
          }
        }
      }
    }
  }

  const records = new Map<string, H2hScoringRecord>();
  for (const team of teams) {
    const available = avail.get(team.teamAbbreviation) ?? 0;
    const points = pts.get(team.teamAbbreviation) ?? 0;
    records.set(team.teamAbbreviation, {
      points,
      available,
      percentage: available > 0 ? points / available : -1,
    });
  }

  return { records, anyGames };
}

export function createH2hScoringStep<T extends TiebreakerTeam>(): TiebreakerStepFn<T> {
  return (teams: T[], ctx): StepResult<T> => {
    if (teams.length < 2) {
      return { winners: [], remaining: teams };
    }

    const { records, anyGames } = calculateH2hScoringRecords(teams, ctx.games);

    if (!anyGames) {
      for (const t of teams) {
        addExplanation(ctx, t, 'H2H Scoring: no games played.');
      }
      return { winners: [], remaining: teams };
    }

    // Teams with no available games (isolated — no H2H at all) get -1.
    interface TeamPct { team: T; pct: number; p: number; a: number }
    const ranked: TeamPct[] = teams.map(t => {
      const record = records.get(t.teamAbbreviation)!;
      return { team: t, pct: record.percentage, p: record.points, a: record.available };
    });

    // Find the highest pct
    const maxPct = Math.max(...ranked.map(r => r.pct));
    const winners = ranked.filter(r => r.pct === maxPct);
    const losers = ranked.filter(r => r.pct < maxPct);

    if (winners.length === teams.length) {
      // All tied — no break
      for (const r of ranked) {
        addExplanation(ctx, r.team, `H2H Scoring: ${r.p}/${r.a} pts (${(r.pct * 100).toFixed(1)}%) — tied.`);
      }
      return { winners: [], remaining: teams };
    }

    for (const r of winners) {
      addExplanation(ctx, r.team, `H2H Scoring: ${r.p}/${r.a} pts (${(r.pct * 100).toFixed(1)}%) — best.`);
    }
    for (const r of losers) {
      addExplanation(ctx, r.team, `H2H Scoring: ${r.p}/${r.a} pts (${(r.pct * 100).toFixed(1)}%).`);
    }

    return {
      winners: winners.map(r => r.team),
      remaining: losers.map(r => r.team),
      rankMethod: RankMethod.HeadToHeadScoring,
    };
  };
}

/**
 * Apply NHL odd-game exclusion for a pair of teams.
 *
 * If one city hosted more games than the other, the earliest game(s) in the
 * city with the extra home game are excluded until counts balance.
 *
 * If any game lacks a sortOrder (no date), exclusion is skipped — the full
 * game list is returned unchanged because chronological ordering is undefined.
 *
 * @param games All games between the two teams (from getGamesBetween).
 * @param homeTeamAbbr The abbreviation used as "team A" (games where homeTeam === this are A's home games).
 * @returns The subset of games to count after exclusion.
 */
export function excludeOddGames(games: GameResult[], homeTeamAbbr: string): GameResult[] {
  if (games.length === 0) return games;

  const hostedByA = games.filter(g => g.homeTeam === homeTeamAbbr);
  const hostedByB = games.filter(g => g.homeTeam !== homeTeamAbbr);
  const diff = hostedByA.length - hostedByB.length;

  if (diff === 0) return games; // balanced — nothing to exclude

  // If any game lacks a sortOrder we cannot determine chronological order — skip exclusion
  if (games.some(g => g.sortOrder == null)) return games;

  const result = [...games];

  if (diff > 0) {
    // A hosted more — exclude the earliest |diff| games hosted by A
    const sortedA = [...hostedByA].sort((x, y) => (x.sortOrder ?? 0) - (y.sortOrder ?? 0));
    for (let k = 0; k < diff; k++) {
      const idx = result.indexOf(sortedA[k]);
      if (idx !== -1) result.splice(idx, 1);
    }
  } else {
    // B hosted more — exclude the earliest |diff| games hosted by B
    const sortedB = [...hostedByB].sort((x, y) => (x.sortOrder ?? 0) - (y.sortOrder ?? 0));
    const excess = -diff;
    for (let k = 0; k < excess; k++) {
      const idx = result.indexOf(sortedB[k]);
      if (idx !== -1) result.splice(idx, 1);
    }
  }

  return result;
}

export const h2hScoringStep = createH2hScoringStep<TiebreakerTeam>();
